import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Euro, Search } from "lucide-react";
import { Input, Label, SearchField, Select } from "@/components/ui";
import { WalletDateRangeInputs } from "@/components/common/WalletDateRangeInputs";
import {
  DeferredFilterSection,
} from "@/components/common/DeferredFilterPanel";
import type { WalletTransactionFilterState } from "@/lib/walletTransactionFilters";

export interface WalletTransactionFilterActivityOption {
  value: string;
  label: string;
}

export interface WalletTransactionFilterLabels {
  dateRange: string;
  from?: string;
  to?: string;
  searchCriteria?: string;
  searchDescriptionLabel: string;
  searchDescriptionPlaceholder: string;
  activityLabel: string;
  allActivities: string;
  amountRange?: string;
  minAmount: string;
  maxAmount: string;
  placeholderMin: string;
  placeholderMax: string;
}

interface WalletTransactionFilterFieldsProps {
  filters: WalletTransactionFilterState;
  labels: WalletTransactionFilterLabels;
  onUpdateField: <K extends keyof WalletTransactionFilterState>(
    field: K,
    value: WalletTransactionFilterState[K],
  ) => void;
  onUpdateDateRange: (range: { startDate: string; endDate: string }) => void;
  activities?: WalletTransactionFilterActivityOption[];
  compact?: boolean;
  inputClassName?: string;
  /** Wallet page uses titled sections for search/amount; transactions only titles the date block. */
  sectionStyle?: "wallet" | "transactions";
  /** Off when the page already shows the period picker elsewhere (wallet header). */
  showDateRange?: boolean;
}

export function WalletTransactionFilterFields({
  filters,
  labels,
  onUpdateField,
  onUpdateDateRange,
  activities,
  compact = false,
  inputClassName,
  sectionStyle = "wallet",
  showDateRange = true,
}: WalletTransactionFilterFieldsProps) {
  const { t: tCommon } = useTranslation("common");
  const fieldId = useId();
  const descriptionInputId = `${fieldId}-description`;
  const activitySelectId = `${fieldId}-activity`;
  const minAmountInputId = `${fieldId}-min-amount`;
  const maxAmountInputId = `${fieldId}-max-amount`;

  const showActivityFilter = activities != null && activities.length > 0;

  const searchAndActivity = (
    <div
      className={
        showActivityFilter ? "grid gap-4 sm:grid-cols-2" : undefined
      }
    >
      <div>
        <Label htmlFor={descriptionInputId} spacing="field">
          {labels.searchDescriptionLabel}
        </Label>
        <SearchField
          id={descriptionInputId}
          value={filters.descriptionFilter}
          onChange={(value) => onUpdateField("descriptionFilter", value)}
          placeholder={labels.searchDescriptionPlaceholder}
          searchLabel={tCommon("search")}
          clearSearchLabel={tCommon("clearSearch")}
          className="max-w-none"
          inputClassName={inputClassName}
        />
      </div>
      {showActivityFilter ? (
        <Select
          id={activitySelectId}
          label={labels.activityLabel}
          value={filters.selectedActivity}
          onChange={(e) => onUpdateField("selectedActivity", e.target.value)}
          options={[{ value: "", label: labels.allActivities }, ...activities]}
          className={inputClassName}
        />
      ) : null}
    </div>
  );

  const amountRange = (
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <Label htmlFor={minAmountInputId} spacing="field">
          {labels.minAmount}
        </Label>
        <Input
          id={minAmountInputId}
          type="number"
          placeholder={labels.placeholderMin}
          min="0"
          step="0.01"
          value={filters.minAmount}
          onChange={(e) => onUpdateField("minAmount", e.target.value)}
          leftIcon={<Euro className="h-4 w-4" />}
          className={inputClassName}
        />
      </div>
      <div>
        <Label htmlFor={maxAmountInputId} spacing="field">
          {labels.maxAmount}
        </Label>
        <Input
          id={maxAmountInputId}
          type="number"
          placeholder={labels.placeholderMax}
          min="0"
          step="0.01"
          value={filters.maxAmount}
          onChange={(e) => onUpdateField("maxAmount", e.target.value)}
          leftIcon={<Euro className="h-4 w-4" />}
          className={inputClassName}
        />
      </div>
    </div>
  );

  return (
    <>
      {showDateRange ? (
        <DeferredFilterSection
          icon={Calendar}
          title={labels.dateRange}
          compact={compact}
        >
          <WalletDateRangeInputs
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={onUpdateDateRange}
            startLabel={labels.from}
            endLabel={labels.to}
            className={compact ? "grid gap-3 sm:grid-cols-2" : undefined}
            inputClassName={inputClassName}
          />
        </DeferredFilterSection>
      ) : null}

      {sectionStyle === "wallet" ? (
        <DeferredFilterSection
          icon={Search}
          title={labels.searchCriteria ?? ""}
          compact={compact}
        >
          {searchAndActivity}
        </DeferredFilterSection>
      ) : (
        <div className="mb-4">{searchAndActivity}</div>
      )}

      {sectionStyle === "wallet" ? (
        <DeferredFilterSection
          icon={Euro}
          title={labels.amountRange ?? ""}
          compact={compact}
          className={compact ? "mb-0" : undefined}
        >
          {amountRange}
        </DeferredFilterSection>
      ) : (
        <div className="mb-4">{amountRange}</div>
      )}
    </>
  );
}
